import { addJobDetailsMetadata } from '@seek/sol-js';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import type { RecommendedGlobalJob } from 'src/queries/types';

// Record<JobId, SolHash>
type SolContextValueType = {
  hashes: Partial<Record<string, string>>;
  setHashes: (hashes: Record<string, string>) => void;
};

const initialState: SolContextValueType = {
  hashes: {},
  setHashes: () => {},
};

const SolContext = createContext<SolContextValueType>(initialState);

export const SolContextProvider = ({ children }: { children: ReactNode }) => {
  const [hashes, setHashes] = useState({});

  return (
    <SolContext.Provider value={{ hashes, setHashes }}>
      {children}
    </SolContext.Provider>
  );
};

const useSolContext = () => useContext(SolContext);

export const useSolHashByJobId = (jobId: string) => {
  const { hashes } = useSolContext();

  const hash = useMemo(() => hashes[jobId], [hashes, jobId]);

  return hash;
};

export const useSetSolHashesToLocalStorage = () => {
  const { setHashes } = useSolContext();

  const setSolHashesToLocalStorage = useCallback(
    (jobs: RecommendedGlobalJob[]) => {
      if (jobs.length) {
        const hashes = addJobDetailsMetadata(jobs);
        const mappedHashes = hashes.reduce<Record<string, string>>(
          (acc, hash, index) => {
            acc[(jobs[index] ?? jobs[0]).job.id] = hash;
            return acc;
          },
          {},
        );
        setHashes(mappedHashes);
      }
    },
    [setHashes],
  );

  return setSolHashesToLocalStorage;
};
